import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import { AideCriminalData, emptyLiveInAideState } from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 6 — Criminal screening (RESPROP LIA Questionnaire p3, "Criminal
// Screening Information"). Five Yes/No questions, all required. When the first
// (legal actions in the past 10 years) is Yes, reveal a required "please
// explain" textarea; when No, the textarea is hidden AND cleared so stale text
// can't leak into the payload. Reuses the affordable Y/N ToggleButton pattern
// (booleans stored as boolean|null, `null` == unanswered) from AideHousehold
// and the conditional-reveal pattern from AideMedicalExpense.
//
// SLICE-KEY QUIRK: the step key is "criminalScreening" but the state field is
// named `criminal`; the container reads/writes defaultValues from
// applicationData.criminalScreening (persisted under the step key).
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted criminal-screening slice (state.criminal). */
  defaultValues?: Partial<AideCriminalData>;
  onSubmit: (data: AideCriminalData) => void | Promise<void>;
  isSaving?: boolean;
}

/** The five Y/N criminal-screening questions, in questionnaire order. */
const QUESTION_KEYS: (keyof AideCriminalData)[] = [
  "legalActions10y",
  "sexOffenderLifetime",
  "sexOffenderOther",
  "marijuana",
  "alcoholAbuse",
];

export const AideCriminalScreening: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const requiredMsg = t(
    "affordable.application.liveInAide.criminalScreening.required"
  ) as string;

  const schema = yup.object({
    legalActions10y: yup.boolean().nullable().required(requiredMsg),
    // Explain is required only when legal actions in the past 10 years = Yes.
    legalActionsExplain: yup.string().when("legalActions10y", {
      is: true,
      then: (s) =>
        s.required(
          t(
            "affordable.application.liveInAide.criminalScreening.explainRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    sexOffenderLifetime: yup.boolean().nullable().required(requiredMsg),
    sexOffenderOther: yup.boolean().nullable().required(requiredMsg),
    marijuana: yup.boolean().nullable().required(requiredMsg),
    alcoholAbuse: yup.boolean().nullable().required(requiredMsg),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AideCriminalData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      legalActions10y:
        defaultValues?.legalActions10y ??
        emptyLiveInAideState.criminal.legalActions10y,
      legalActionsExplain:
        defaultValues?.legalActionsExplain ??
        emptyLiveInAideState.criminal.legalActionsExplain,
      sexOffenderLifetime:
        defaultValues?.sexOffenderLifetime ??
        emptyLiveInAideState.criminal.sexOffenderLifetime,
      sexOffenderOther:
        defaultValues?.sexOffenderOther ??
        emptyLiveInAideState.criminal.sexOffenderOther,
      marijuana:
        defaultValues?.marijuana ?? emptyLiveInAideState.criminal.marijuana,
      alcoholAbuse:
        defaultValues?.alcoholAbuse ??
        emptyLiveInAideState.criminal.alcoholAbuse,
    },
  });

  const legalActions10y = watch("legalActions10y");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.criminalScreening.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.criminalScreening.subtitle")}
        </Typography>

        {QUESTION_KEYS.map((key) => (
          <React.Fragment key={key}>
            <Controller
              name={key}
              control={control}
              render={({ field }) => (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                      {t(
                        `affordable.application.liveInAide.criminalScreening.questions.${key}`
                      )}
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={
                        field.value === true
                          ? "yes"
                          : field.value === false
                          ? "no"
                          : null
                      }
                      onChange={(_, val) => {
                        if (val === null) return;
                        const yes = val === "yes";
                        field.onChange(yes);
                        // When legal-actions switches to No, clear the revealed
                        // explanation so stale text can't leak into the payload.
                        if (key === "legalActions10y" && !yes) {
                          setValue("legalActionsExplain", "");
                        }
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="yes" sx={{ px: 2 }}>
                        {t(
                          "affordable.application.liveInAide.criminalScreening.yes"
                        )}
                      </ToggleButton>
                      <ToggleButton value="no" sx={{ px: 2 }}>
                        {t(
                          "affordable.application.liveInAide.criminalScreening.no"
                        )}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {errors[key] && (
                    <FormHelperText error>
                      {errors[key]?.message as string}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />

            {/* Revealed only when legal actions in the past 10 years = Yes. */}
            {key === "legalActions10y" && legalActions10y === true && (
              <Controller
                name="legalActionsExplain"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    label={`${t(
                      "affordable.application.liveInAide.criminalScreening.explainLabel"
                    )} *`}
                    error={!!errors.legalActionsExplain}
                    helperText={errors.legalActionsExplain?.message}
                    sx={{ mb: 3 }}
                  />
                )}
              />
            )}
          </React.Fragment>
        ))}
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
